"use client"
import { EditableCell } from "@/components/editable-cell"
import { DropdownCell } from "@/components/dropdown-cell"
import { BooleanCell } from "@/components/boolean-cell"
import type { WorkOrder, FieldConfiguration, WorkflowStage } from "@/types/workflow"
import { Avatar, AvatarFallback, getInitials, getAvatarColor } from "@/components/ui/avatar"
import { MultiSelectCell } from "@/components/multi-select-cell"
import { useTranslation } from "@/hooks/use-translation"
import { getLocalizedField } from "@/utils/language-utils"
import { useLanguage } from "@/context/language-context"
import { useWorkflow } from '@/context/workflow-context-simple'
import { useCodeValues } from '@/hooks/use-code-values'
import React, { useRef, useLayoutEffect, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import { createPortal } from "react-dom"
import { getDisplayValueFromColumn } from '@/utils/column-type-helpers'

const MONTHS = {
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ],
  fr: [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ]
}

interface GridCellProps {
  order: WorkOrder
  column: FieldConfiguration
  stage: WorkflowStage
  isEditing: boolean
  onEdit: () => void
  onSave: (value: any) => void
  onCancel: () => void
  fieldNameOverride?: string
  canEditFieldData?: boolean
}

const formatDateSafely = (dateValue: any, fallback = ""): string => {
  if (!dateValue) return fallback
  try {
    let date: Date
    if (dateValue instanceof Date) {
      // If it's already a Date object, create a new UTC date
      date = new Date()
      date.setUTCFullYear(dateValue.getUTCFullYear())
      date.setUTCMonth(dateValue.getUTCMonth())
      date.setUTCDate(dateValue.getUTCDate())
      date.setUTCHours(0, 0, 0, 0)
    } else if (typeof dateValue === "string") {
      // Parse the UTC date string correctly
      const dateOnly = dateValue.split('T')[0]
      const [year, month, day] = dateOnly.split('-').map(Number)
      date = new Date()
      date.setUTCFullYear(year)
      date.setUTCMonth(month - 1) // Month is 0-indexed
      date.setUTCDate(day)
      date.setUTCHours(0, 0, 0, 0)
    } else {
      return fallback
    }
    if (isNaN(date.getTime())) {
      return fallback
    }
    const { language } = useLanguage();
    // Format the date using UTC values
    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = MONTHS[language][date.getUTCMonth()].slice(0, 3)
    const year = date.getUTCFullYear()
    return `${day} ${month} ${year}`
  } catch (error) {
    console.warn(`Error formatting date "${dateValue}":`, error)
    return fallback
  }
}

function getCellValue(order: any, columnCode: string): any {
  if (!order || !columnCode) return '';
  const targetKey = columnCode.toLowerCase();
  const foundKey = Object.keys(order).find(key => key.toLowerCase() === targetKey);
  return foundKey !== undefined ? order[foundKey] : '';
}

// Helper to format date as MM-DD-YYYY (en) or DD-MM-YYYY (fr)
function formatDateOnly(dateValue: string, language: string): string {
  if (!dateValue) return '';
  const dateOnly = dateValue.split('T')[0];
  const [year, month, day] = dateOnly.split('-');
  if (language === 'fr') {
    return `${day}-${month}-${year}`;
  }
  return `${month}-${day}-${year}`;
}

// Helper to format datetime with UTC to local conversion
function formatDateTime(dateValue: string, language: string): string {
  if (!dateValue) return '';
  try {
    // Parse the UTC date string
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    // Get local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    if (language === 'fr') {
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    }
    return `${month}-${day}-${year} ${hours}:${minutes}`;
  } catch (error) {
    console.warn('Error formatting datetime:', error);
    return '';
  }
}

export function GridCell({ order, column, stage, isEditing, onEdit, onSave, onCancel, fieldNameOverride, canEditFieldData = false }: GridCellProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { assignedUsers } = useWorkflow();
  const codeValues = useCodeValues();
  const fieldName = fieldNameOverride || column.columnCode;
  const value = getCellValue(order, fieldName);
  // Cell is editable only if both the column allows editing AND the user has the EditFieldData permission
  const isFieldEditable = column.isEditable && canEditFieldData;

  // Helper function to get display value for CodeValueSource columns
  const getDisplayValueForCodeValueSource = (cellValue: any, column: FieldConfiguration): string => {
    if (!column.codeValueSource || !codeValues.isReady || !cellValue) {
      return cellValue;
    }
    
    try {
      const displayText = codeValues.getCodeValueTextByCode(String(cellValue), column.codeValueSource);
      return displayText || cellValue; // Fallback to original value if no text found
    } catch (error) {
      return cellValue;
    }
  };

  // --- FIX: Always call useState for search at the top ---
  const [search, setSearch] = React.useState("");
  const cellRef = useRef<HTMLSpanElement>(null);
  const [popoverPos, setPopoverPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  useLayoutEffect(() => {
    if (isPopoverOpen && cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!isPopoverOpen) setSearch("");
    if (!isPopoverOpen && isEditing) onCancel();
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (cellRef.current && !cellRef.current.contains(e.target as Node)) {
        setIsPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isPopoverOpen]);

  // Special handling for AssignedToUser column
  if (
    fieldName.toLowerCase() === "assignedtouser" ||
    (typeof value === 'object' && value && (value.email || value.fullName))
  ) {
    // Value can be an object or string (email)
    let email = '';
    if (typeof value === 'object' && value && value.email) {
      email = value.email;
    } else if (typeof value === 'string') {
      email = value;
    }
    // If assignedUsers is not loaded yet, show an empty cell
    if (!assignedUsers || assignedUsers.length === 0) {
      return (
        <div className="block text-left px-2 py-1"></div>
      );
    }
    const user = assignedUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
    let displayName = '';
    if (user) {
      const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
      displayName = toTitleCase(user.fullName || '');
    } else {
      displayName = email || '';
    }
    if (isEditing) {
      // Custom searchable dropdown for AssignedToUser, rendered inside the cell
      const normalize = (str: string) => (str || "").toLowerCase().replace(/\s+/g, " ").trim();
      const filteredUsers = assignedUsers.filter(u => {
        const fullName = normalize(u.fullName);
        const email = normalize(u.email);
        const searchNorm = normalize(search);
        return searchNorm
          .split(" ")
          .filter(Boolean)
          .every(word => fullName.includes(word) || email.includes(word));
      }).sort((a, b) =>
        (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase())
      );
      // Find the current selected user
      const selectedUser = assignedUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
      const selectedLabel = selectedUser ? (selectedUser.fullName ? selectedUser.fullName.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()) : selectedUser.email) : null;
      return (
        <span ref={cellRef} className="relative block w-full" style={{ minHeight: 40 }}>
          <div
            className="absolute left-0 top-0 z-[2002] bg-white border border-gray-300 rounded shadow max-h-60 overflow-y-auto w-full"
            style={{ minWidth: 0 }}
          >
            {/* Show current selection at the top */}
            {selectedUser && (
              <div className="flex items-center gap-2 px-2 py-2 border-b border-gray-100 bg-gray-50">
                <Avatar className="h-6 w-6">
                  <AvatarFallback bgColor={getAvatarColor(selectedLabel!)} initials={getInitials(selectedLabel!)} />
                </Avatar>
                <span>{selectedLabel}</span>
              </div>
            )}
            <input
              className="block w-full px-2 py-1 mb-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Type to filter..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              onBlur={onCancel}
            />
            {filteredUsers.length === 0 && (
              <div className="px-2 py-2 text-gray-400">No users found</div>
            )}
            {filteredUsers.map((u, idx) => {
              const label = u.fullName ? u.fullName.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()) : u.email;
              return (
                <div
                  key={u.email + '-' + idx}
                  className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-100"
                  title={u.email}
                  onMouseDown={e => {
                    e.preventDefault();
                    if (u.email !== email) {
                      onSave(u);
                    }
                    setIsPopoverOpen(false);
                  }}
                >
                  <Avatar className="h-6 w-6">
                    <AvatarFallback bgColor={getAvatarColor(label)} initials={getInitials(label)} />
                  </Avatar>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </span>
      );
    }
    const tooltip = email ? email : undefined;
    // Make cell clickable for editing, add dropdown arrow
    return (
      <>
        <span
          ref={cellRef}
          className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50 rounded justify-between"
          title={tooltip}
          onClick={() => setIsPopoverOpen(true)}
          tabIndex={0}
          role="button"
          style={{ minHeight: 32 }}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarFallback bgColor={getAvatarColor(displayName || 'Unassigned')} initials={getInitials(displayName || 'Unassigned')} />
            </Avatar>
            <span className="truncate">{displayName || <span className="text-gray-400">Unassigned</span>}</span>
          </span>
          <ChevronDown className="ml-2 w-4 h-4 text-gray-400 flex-shrink-0" />
        </span>
        {isPopoverOpen && popoverPos &&
          createPortal(
            <div
              className="absolute z-[2000] bg-white border border-gray-300 rounded shadow max-h-60 overflow-y-auto"
              style={{
                top: popoverPos.top,
                left: popoverPos.left,
                width: popoverPos.width,
                minWidth: 200,
              }}
            >
              <input
                className="block w-full px-2 py-1 mb-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Type to filter..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
              {assignedUsers.filter(u => {
                const label = u.fullName ? u.fullName.toLowerCase() : "";
                const email = u.email ? u.email.toLowerCase() : "";
                return (
                  !search ||
                  label.includes(search.toLowerCase()) ||
                  email.includes(search.toLowerCase())
                );
              }).map((u, idx) => {
                const label = u.fullName ? u.fullName.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()) : u.email;
                return (
                  <div
                    key={u.email + '-' + idx}
                    className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-100"
                    title={u.email}
                    onMouseDown={e => {
                      e.preventDefault();
                      if (u.email !== email) {
                        onSave(u);
                      }
                      setIsPopoverOpen(false);
                    }}
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarFallback bgColor={getAvatarColor(label)} initials={getInitials(label)} />
                    </Avatar>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>,
            document.body
          )}
      </>
    );
  }

  // Handle TextList with or without allowMultipleSelections
  if (column.columnTypeCode === "TextList") {
    if (column.allowMultipleSelections) {
      // Value is expected to be an array of codes or objects
      const valueArray = Array.isArray(value) ? value : (typeof value === 'string' && value ? [value] : []);
      const options = (column.customValues || []).map(opt => ({
        value: String(opt.code),
        label: getLocalizedField(opt, 'title', language),
        color: opt.color || undefined,
      }));

      if (isEditing) {
        return (
          <MultiSelectCell
            value={valueArray}
            options={options}
            column={column}
            rowId={String(order.id ?? "")}
            onSave={onSave}
            onCancel={onCancel}
          />
        );
      } else {
        let display = "";
        if (typeof column.subColumnName === 'string' && column.subColumnName) {
          // Join subColumnName from each object in the array (case-insensitive)
          display = valueArray
            .map(item => {
              if (item && typeof item === 'object') {
                const key = Object.keys(item).find(k => k.toLowerCase() === column.subColumnName!.toLowerCase());
                return key ? item[key] : "";
              }
              return String(item);
            })
            .filter(Boolean)
            .join(", ");
        } else {
          // Map codes to their translated labels, then join
          display = valueArray.map(item => {
            const code = String(item);
            
            // If column has codeValueSource, use code values from the service
            if (column.codeValueSource && codeValues.isReady) {
              try {
                const displayText = codeValues.getCodeValueTextByCode(code, column.codeValueSource);
                if (displayText) {
                  return displayText;
                }
              } catch (error) {
                console.warn('Failed to get code value text for TextList:', error);
              }
            }
            
            // Otherwise, use customValues
            const match = column.customValues?.find(opt => opt.code === code);
            return match ? getLocalizedField(match, 'title', language) : code;
          }).join(", ");
        }
        return (
          isFieldEditable ?
            (<div className="block text-left px-2 py-1 cursor-pointer" onClick={onEdit} title={t('clickToEdit')}>
              {display || (<span className="text-gray-400">{t('selectOptions')}</span>)}
            </div>)
          :
            (<div className="block text-left px-2 py-1">
              {display}
            </div>)
        );
      }
    } else {
      // TextList without allowMultipleSelections - handle comma-separated codes
      if (value && typeof value === 'string' && (column.customValues || column.codeValueSource)) {
        const codes = value.split(',').map(code => code.trim());
        const displayTexts = codes.map(code => {
          // If column has codeValueSource, use code values from the service
          if (column.codeValueSource && codeValues.isReady) {
            try {
              const displayText = codeValues.getCodeValueTextByCode(code, column.codeValueSource);
              if (displayText) {
                return displayText;
              }
            } catch (error) {
              console.warn('Failed to get code value text for TextList:', error);
            }
          }
          
          // Otherwise, use customValues
          const match = column.customValues?.find((opt: any) => opt.code === code);
          return match ? getLocalizedField(match, 'title', language) : code;
        });
        const display = displayTexts.join(', ');
        
        return isFieldEditable ? (
          <div className="block text-left px-2 py-1 cursor-pointer" onClick={onEdit} title={t('clickToEdit')}>
            {display}
          </div>
        ) : (
          <div className="block text-left px-2 py-1">
            {display}
          </div>
        );
      }
    }
  }

  if (isEditing) {
    return <EditableCell value={String(value ?? "")} column={column} rowId={String(order.id ?? "")} onSave={onSave} onCancel={onCancel} />;
  }

  // Dropdowns for columns with options
  if (column.customValues && column.customValues.length > 0) {
    if (isFieldEditable) {
      return (
        <div className="block text-left px-2 py-1">
          <DropdownCell
            value={String(value ?? "")}
            options={column.customValues.map(opt => ({ 
              value: getLocalizedField(opt, 'title', language),
              label: getLocalizedField(opt, 'title', language),
              color: String(opt.color ?? "") 
            }))}
            customValues={column.customValues}
            rowId={String(order.id ?? "")}
            column={column}
            onSave={onSave}
          />
        </div>
      );
    } else {
      // Not editable: show value as plain text, with color if defined
      const match = value ? column.customValues.find(opt => opt.code === String(value)) : undefined;
      const display = match ? getLocalizedField(match, 'title', language) : value;
      const color = match && match.color ? match.color : undefined;
          return (
      <div className="block text-left px-2 py-1 truncate" style={color ? { backgroundColor: color, borderRadius: 8, textAlign: 'center' } : {}} title={display}>
        {display}
      </div>
    );
    }
  }

  // Date
  if (column.customValuesType?.toLowerCase() === "date" || column.columnTypeCode?.toLowerCase() === "datetime") {
    if (isEditing) {
      return <EditableCell value={value} column={column} rowId={String(order.id ?? "")} onSave={onSave} onCancel={onCancel} />;
    }
    
    // Determine format based on fieldFormatting (default to ShortDate)
    const formatting = column.fieldFormatting || "ShortDate";
    const formattedValue = formatting === "DateTime" 
      ? formatDateTime(value, language)
      : formatDateOnly(value, language);
    
    // Not editing: show formatted date only
    if (!isFieldEditable) {
      return value ? (
        <span className="block text-center px-2 py-1 truncate" title={formattedValue}>
          {formattedValue}
        </span>
      ) : null;
    }
    // Editable but not in edit mode: apply formatting based on fieldFormatting
    const editableFormatting = column.fieldFormatting || "ShortDate";
    const displayValue = value 
      ? (editableFormatting === "DateTime" ? formatDateTime(value, language) : formatDateOnly(value, language))
      : t('selectDate');
    const titleValue = value
      ? (editableFormatting === "DateTime" ? formatDateTime(value, language) : formatDateOnly(value, language))
      : t('selectDate');
    
    return (
      <div className="cursor-pointer hover:bg-gray-50 rounded" onClick={onEdit} title={t('clickToEdit')}>
        <div className="flex items-center border border-gray-300 rounded px-3 py-2 min-w-[180px] bg-white">
          <span className="mr-2 text-gray-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2" stroke="currentColor" fill="none"/><path d="M16 2v4M8 2v4M3 10h18" strokeWidth="2" stroke="currentColor" fill="none"/></svg>
          </span>
          <span className={`truncate ${value ? "text-black" : "text-gray-400"}`} title={titleValue}>
            {displayValue}
          </span>
        </div>
      </div>
    );
  }

  // Boolean
  if (column.columnTypeCode.toLowerCase() === "boolean" || column.customValuesType === "YesNo" || column.customValuesType === "boolean") {
    return (
      <div className="block text-left px-2 py-1 truncate">
        <BooleanCell 
          value={value} 
          rowId={String(order.id ?? "")} 
          onSave={onSave}
          isEditable={isFieldEditable}
        />
      </div>
    );
  }

  // Checklist column type
  if (column.columnTypeCode?.toLowerCase() === 'checklist') {
    let completedCount = 0;
    let totalCount = 0;
    
    try {
      let items: Array<{ isCompleted: boolean; isDeleted: boolean }> = [];
      
      if (Array.isArray(value)) {
        // Backend returns array directly
        items = value;
      } else if (value && typeof value === 'object' && 'items' in value) {
        // Legacy format with items property
        items = (value as any).items || [];
      } else if (value && typeof value === 'string') {
        const parsed = JSON.parse(value);
        items = Array.isArray(parsed) ? parsed : (parsed.items || []);
      }
      
      // Filter out deleted items for display
      const activeItems = items.filter(item => !item.isDeleted);
      totalCount = activeItems.length;
      completedCount = activeItems.filter(item => item.isCompleted).length;
    } catch (error) {
      console.warn('Failed to parse checklist data:', error);
    }
    
    return (
      <div 
        className="block text-center px-2 py-1 cursor-pointer hover:bg-blue-50 rounded font-medium text-blue-600"
        onClick={onEdit}
        title="Click to open checklist"
      >
        {totalCount > 0 ? `${completedCount}/${totalCount}` : '0/0'}
      </div>
    );
  }

  // Default: text
  // If this is a FullName field, render avatar + name
  if ((fieldName.toLowerCase().includes("fullname") || column.columnNameEnglish?.toLowerCase().includes("fullname")) && value) {
    return (
      <span className="flex items-center gap-2 px-2 py-1">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarFallback bgColor={getAvatarColor(String(value))} initials={getInitials(String(value))} />
        </Avatar>
        <span className="truncate" title={String(value)}>{String(value)}</span>
      </span>
    );
  }

  // Handle CodeValueSource columns - this should come before the default text display
  if (column.codeValueSource && codeValues.isReady) {
    const displayValue = getDisplayValueForCodeValueSource(value, column);
    if (isFieldEditable) {
          return (
      <div className="block text-left px-2 py-1 cursor-pointer hover:bg-gray-50 rounded truncate" onClick={onEdit} title={t('clickToEdit')}>
        {displayValue ?? ""}
      </div>
    );
    }
    return (
      <div className="block text-left px-2 py-1 truncate" title={displayValue ?? ""}>
        {displayValue ?? ""}
      </div>
    );
  }

  // Default text display: allow inline edit if isFieldEditable
  if (isFieldEditable) {
    const displayText = getDisplayValueFromColumn(value, column);
    return (
      <div className="block text-left px-2 py-1 cursor-pointer hover:bg-gray-50 rounded truncate" onClick={onEdit} title={t('clickToEdit')}>
        {displayText}
      </div>
    );
  }
  const displayText = getDisplayValueFromColumn(value, column);
  return (
    <div className="block text-left px-2 py-1 truncate" title={displayText}>
      {displayText}
    </div>
  );
}
